import React from 'react';
import { Box, ButtonBase, Divider, Tooltip, Typography } from '@mui/material';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import TouchAppOutlined from '@mui/icons-material/TouchAppOutlined';
import TitleOutlined from '@mui/icons-material/TitleOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import ViewCarouselOutlined from '@mui/icons-material/ViewCarouselOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';

/** ID các khu vực trong LandingPageCanvasEditor — dùng scrollIntoView; rail thêm phần tử chỉ bật panel */
export const LANDING_CANVAS_SCROLL_IDS = {
  page: 'landing-canvas-section-page',
  library: 'landing-canvas-section-library',
  addButton: 'landing-canvas-rail-add-button',
  addShape: 'landing-canvas-rail-add-shape',
  addEventList: 'landing-canvas-rail-add-event-list',
  addImageSlider: 'landing-canvas-rail-add-slide',
  detail: 'landing-canvas-section-detail',
} as const;

type Props = {
  /** Id vừa bấm — tô sáng */
  highlightId: string | null;
  onGo: (elementId: string) => void;
  /** Chưa chọn trang canvas — Trang, Tải lên, thêm phần tử */
  disableWithoutSection: boolean;
  /** Chưa chọn khu vực canvas — Chi tiết (mở thư viện cột trái) */
  disableDetail: boolean;
};

const railBtnSx = (active: boolean, disabled: boolean) => ({
  width: '100%',
  py: 1.25,
  px: 0.5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.35,
  borderRadius: 1,
  color: disabled ? 'text.disabled' : 'text.secondary',
  bgcolor: active && !disabled ? 'action.selected' : 'transparent',
  '&:hover': disabled ? {} : { bgcolor: 'action.hover' },
});

const LandingCanvasLeftRail: React.FC<Props> = ({
  highlightId,
  onGo,
  disableWithoutSection,
  disableDetail,
}) => {
  const Item = ({
    id,
    label,
    icon,
    disabled,
  }: {
    id: string;
    label: string;
    icon: React.ReactNode;
    disabled: boolean;
  }) => (
    <Tooltip title={label} placement="right">
      <span style={{ width: '100%' }}>
        <ButtonBase
          disabled={disabled}
          onClick={() => onGo(id)}
          sx={railBtnSx(highlightId === id, disabled)}
        >
          <Box sx={{ color: disabled ? 'text.disabled' : 'primary.main', '& svg': { fontSize: 26 } }}>{icon}</Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              lineHeight: 1.15,
              textAlign: 'center',
              px: 0.25,
              wordBreak: 'break-word',
              maxWidth: '100%',
            }}
          >
            {label}
          </Typography>
        </ButtonBase>
      </span>
    </Tooltip>
  );

  return (
    <Box
      component="nav"
      aria-label="Thanh công cụ canvas"
      sx={{
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        flexShrink: 0,
        flexGrow: 0,
        alignSelf: 'stretch',
        bgcolor: '#f7f8f9',
        borderRight: '1px solid',
        borderColor: 'divider',
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.25,
        overflowY: 'auto',
      }}
    >
      <Item
        id={LANDING_CANVAS_SCROLL_IDS.page}
        label="Trang"
        disabled={disableWithoutSection}
        icon={<ArticleOutlined />}
      />
      <Item
        id={LANDING_CANVAS_SCROLL_IDS.library}
        label="Tải lên"
        disabled={disableWithoutSection}
        icon={<CloudUploadOutlined />}
      />
      <Item
        id={LANDING_CANVAS_SCROLL_IDS.addButton}
        label="Nút bấm"
        disabled={disableWithoutSection}
        icon={<TouchAppOutlined />}
      />
      <Item
        id={LANDING_CANVAS_SCROLL_IDS.addShape}
        label="Văn bản"
        disabled={disableWithoutSection}
        icon={<TitleOutlined />}
      />
      <Item
        id={LANDING_CANVAS_SCROLL_IDS.addEventList}
        label="Sự kiện"
        disabled={disableWithoutSection}
        icon={<EventOutlined />}
      />
      <Item
        id={LANDING_CANVAS_SCROLL_IDS.addImageSlider}
        label="Dải ảnh cuộn"
        disabled={disableWithoutSection}
        icon={<ViewCarouselOutlined />}
      />
      <Divider sx={{ mx: 'auto', my: 0.75, width: '72%' }} />
      <Item
        id={LANDING_CANVAS_SCROLL_IDS.detail}
        label="Chi tiết"
        disabled={disableDetail}
        icon={<TuneOutlined />}
      />
    </Box>
  );
};

export default LandingCanvasLeftRail;
